// @ts-nocheck
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import CelebrationEffect from './CelebrationEffect';
import ShimmerButton from './ShimmerButton';
import { CouponData } from '../types/campaign';
import { analytics, AnalyticsEvents } from '../utils/analytics';

interface PairingSuccessProps {
    userEmail: string;
    userId: string;
    showDownloadButton?: boolean;
    savedForLater?: boolean;
    couponData?: CouponData | null;
    previewPassPending?: boolean;
    isSubscribingToChannel?: boolean;
    subscriptionComplete?: boolean;
    disableCelebration?: boolean;
}

export default function PairingSuccess({
    userEmail,
    userId,
    showDownloadButton = false,
    savedForLater = false,
    couponData = null,
    previewPassPending = false,
    isSubscribingToChannel = false,
    subscriptionComplete = false,
    disableCelebration = false,
}: PairingSuccessProps) {
    const [showCelebration, setShowCelebration] = useState(false);
    const router = useRouter();

    useEffect(() => {
        // Preload the Niio logo to avoid visual glitch
        const img = new Image();
        img.src = '/niio_logo_white.png';

        // Trigger celebration effect when component mounts (unless disabled)
        // Also stop celebration if it becomes disabled (e.g., when modals open)
        if (disableCelebration) {
            setShowCelebration(false);
        } else {
            setShowCelebration(true);
        }
    }, [disableCelebration]);

    // Build the download URL with query parameters
    const buildDownloadUrl = () => {
        // Use coupon's onelink if available, otherwise use default
        const baseUrl =
            couponData?.onelink || 'https://niioart.onelink.me/QVcR/artgift';
        const params = new URLSearchParams();

        // Add all original query parameters from the current URL, excluding device-related ones
        Object.entries(router.query).forEach(([key, value]) => {
            if (
                value &&
                key !== 'deviceId' &&
                key !== 'deviceCode' &&
                key !== 'shortSecret'
            ) {
                params.append(key, Array.isArray(value) ? value[0] : value);
            }
        });

        // Add userId if it exists
        if (userId) {
            params.append('userId', userId);
        }
        params.append('newUser', 'true');

        return `${baseUrl}?${params.toString()}`;
    };

    return (
        <>
            <CelebrationEffect
                trigger={showCelebration}
                onComplete={() => {}}
                savedForLater={savedForLater}
            />

            <div className="text-center space-y-6">
                {savedForLater ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="space-y-6"
                    >
                        <div>
                            <h3 className="text-3xl font-bold text-white mb-2">
                                Content Saved!
                            </h3>
                            <p className="text-gray-400 text-lg">
                                Ready when you are
                            </p>
                        </div>

                        {/* Clear 3-step process */}
                        <div className="space-y-4">
                            <div className="flex items-start gap-4 text-left">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    1
                                </div>
                                <div>
                                    <p className="text-white font-medium">
                                        Scan the QR when you're in front of a
                                        screen
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 text-left">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    2
                                </div>
                                <div>
                                    <p className="text-white font-medium">
                                        Review the saved art
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 text-left">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    3
                                </div>
                                <div>
                                    <p className="text-white font-medium">
                                        Send the selected content to your art
                                        screen
                                    </p>
                                </div>
                            </div>
                        </div>

                        {isSubscribingToChannel ? (
                            <div className="mt-4">
                                <p className="text-white font-medium text-center">
                                    Adding artworks to your favorites...
                                </p>
                                <div className="flex justify-center mt-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                                </div>
                            </div>
                        ) : previewPassPending ? (
                            <div className="mt-4">
                                <p className="text-white font-medium text-center">
                                    Preparing your special offer...
                                </p>
                                <div className="flex justify-center mt-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {subscriptionComplete && (
                                    <div className="bg-purple-900/30 rounded-xl p-4 backdrop-blur-sm border border-purple-500/30 mt-4">
                                        <p className="text-purple-300 font-medium text-center">
                                            ✨ Your selected artcast and
                                            artworks have been added to your
                                            favorites!
                                        </p>
                                        <p className="text-purple-200 text-sm text-center mt-2">
                                            Access them anytime in "My Area" →
                                            "Favorites"
                                        </p>
                                    </div>
                                )}
                                <p className="text-gray-400 text-sm mt-4">
                                    You can also download the Niio app to
                                    explore more.
                                </p>
                            </>
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="space-y-4"
                    >
                        {isSubscribingToChannel ? (
                            <div className="bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                                <p className="text-white font-medium text-center">
                                    Adding artworks to your favorites...
                                </p>
                                <div className="flex justify-center mt-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                                </div>
                            </div>
                        ) : previewPassPending ? (
                            <div className="bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                                <p className="text-white font-medium text-center">
                                    Preparing your special offer...
                                </p>
                                <div className="flex justify-center mt-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {subscriptionComplete && (
                                    <div className="bg-purple-900/30 rounded-xl p-4 backdrop-blur-sm border border-purple-500/30">
                                        <p className="text-purple-300 font-medium text-center">
                                            ✨ Your selected artcast and
                                            artworks have been added to your
                                            favorites!
                                        </p>
                                        <p className="text-purple-200 text-sm text-center mt-2">
                                            Access them anytime in "My Area" →
                                            "Favorites"
                                        </p>
                                    </div>
                                )}

                                {/* What's next steps for successfully paired users */}
                                <div className="space-y-4 mt-4">
                                    <p className="text-gray-300 font-medium text-center mb-3">
                                        What's Next:
                                    </p>
                                    <div className="flex items-start gap-4 text-left">
                                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                            1
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">
                                                Your art is now displaying on
                                                your screen
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 text-left">
                                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                            2
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">
                                                Download the app to control and
                                                customize
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 text-left">
                                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                            3
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">
                                                Explore more content and
                                                features
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}

                {/* Always render download button but animate visibility */}
                {showDownloadButton && !savedForLater && (
                    <motion.div
                        className="mt-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                            opacity:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 1
                                    : 0,
                            y:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 0
                                    : 20,
                            pointerEvents:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 'auto'
                                    : 'none',
                        }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                        <a
                            href={buildDownloadUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-block"
                            onClick={() => {
                                analytics.capture(
                                    AnalyticsEvents.APP_DOWNLOAD_CLICKED,
                                    {
                                        location: 'pairing_success',
                                        saved_for_later: false,
                                        user_id: userId,
                                    }
                                );
                            }}
                        >
                            <ShimmerButton className="w-full text-lg py-3">
                                <span className="flex items-center justify-center gap-2">
                                    Download
                                    <img
                                        src="/niio_logo_white.png"
                                        alt="NIIO"
                                        className="h-4 w-auto inline-block"
                                        style={{ filter: 'brightness(2)' }}
                                    />
                                    App
                                </span>
                            </ShimmerButton>
                        </a>
                    </motion.div>
                )}

                {/* Always render download button for savedForLater but animate visibility */}
                {savedForLater && (
                    <motion.div
                        className="mt-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                            opacity:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 1
                                    : 0,
                            y:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 0
                                    : 20,
                            pointerEvents:
                                !previewPassPending && !isSubscribingToChannel
                                    ? 'auto'
                                    : 'none',
                        }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                        <a
                            href={buildDownloadUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-block"
                            onClick={() => {
                                analytics.capture(
                                    AnalyticsEvents.APP_DOWNLOAD_CLICKED,
                                    {
                                        location: 'pairing_success',
                                        saved_for_later: true,
                                        user_id: userId,
                                    }
                                );
                            }}
                        >
                            <ShimmerButton className="w-full text-lg py-3">
                                <span className="flex items-center justify-center gap-2">
                                    Download
                                    <img
                                        src="/niio_logo_white.png"
                                        alt="NIIO"
                                        className="h-4 w-auto inline-block"
                                        style={{ filter: 'brightness(2)' }}
                                    />
                                    App
                                </span>
                            </ShimmerButton>
                        </a>
                    </motion.div>
                )}

                <motion.p
                    className="text-sm text-gray-500 mt-6"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    {savedForLater
                        ? 'Your art is waiting ✨'
                        : 'Enjoy your digital art experience ✨'}
                </motion.p>
            </div>
        </>
    );
}
